import { useCallback, useEffect, useRef } from 'react'

type UseInfiniteScrollOptions = {
    hasMore: boolean
    isLoading: boolean
    onLoadMore: () => void | Promise<void> | Promise<any>
    root?: Element | null
    rootMargin?: string
    threshold?: number
}

const useInfiniteScroll = ({ hasMore, isLoading, onLoadMore, root = null, rootMargin = '200px', threshold = 0 }: UseInfiniteScrollOptions) => {
    const observerRef = useRef<IntersectionObserver | null>(null)
    const lastElementRef = useRef<Element | null>(null)

    const onLoadMoreRef = useRef(onLoadMore)
    const hasMoreRef = useRef(hasMore)
    const isLoadingRef = useRef(isLoading)
    const rootRef = useRef<Element | null>(root)

    const lockRef = useRef(false)

    useEffect(() => {
        onLoadMoreRef.current = onLoadMore
    }, [onLoadMore])

    useEffect(() => {
        hasMoreRef.current = hasMore
    }, [hasMore])

    useEffect(() => {
        isLoadingRef.current = isLoading
    }, [isLoading])

    useEffect(() => {
        rootRef.current = root
    }, [root])

    useEffect(() => {
        const observer = new IntersectionObserver(
            async ([entry]) => {
                if (!entry.isIntersecting || !hasMoreRef.current || isLoadingRef.current || lockRef.current) {
                    return
                }

                lockRef.current = true

                try {
                    await onLoadMoreRef.current()
                } finally {
                    lockRef.current = false
                }
            },
            {
                root: rootRef.current,
                rootMargin,
                threshold
            }
        )

        observerRef.current = observer

        const node = lastElementRef.current
        if (node) observer.observe(node)

        return () => {
            observer.disconnect()
            observerRef.current = null
        }
    }, [rootMargin, threshold])

    const lastElementRefCallback = useCallback((node: Element | null) => {
        const observer = observerRef.current

        if (!observer) return

        if (lastElementRef.current) {
            observer.unobserve(lastElementRef.current)
        }

        lastElementRef.current = node

        if (node) {
            observer.observe(node)
        }
    }, [])

    return { lastElementRef: lastElementRefCallback }
}

export default useInfiniteScroll
